import axios from 'axios';
import {API_URL, MEDIA_TYPE_V2} from '../config/constants';

/** A billed model and its list price, in US dollars per million tokens. */
export interface ModelPrice {
    model: string;
    inputPerMillion: number;
    cachedPerMillion: number;
    outputPerMillion: number;
}

/** One model's usage; costUsd is null for a model the provider does not price. */
export interface ModelUsage {
    model: string;
    calls: number;
    errors: number;
    promptTokens: number;
    cachedTokens: number;
    completionTokens: number;
    costUsd: number | null;
}

/** One model's usage within a recorded run, as the usage log writes it. */
export interface RunModelUsage {
    calls?: number;
    errors?: number;
    promptTokens?: number;
    cachedTokens?: number;
    completionTokens?: number;
    costUsd?: number | null;
}

/**
 * One line of the usage log. Lines are written by several jobs, and older lines predate costs, so
 * every field is optional.
 */
export interface UsageRun {
    recordedAt?: string;
    kind?: string;
    job?: string;
    jobExecutionId?: number;
    status?: string;
    irpId?: number;
    irpName?: string;
    source?: string;
    minutes?: number;
    costUsd?: number;
    llmUsage?: Record<string, RunModelUsage>;
}

/** What the provider says about itself: never its key. Fields a provider has no use for are absent. */
export interface ProviderDescription {
    provider?: string;
    region?: string;
    fastModel?: string;
    fastServiceTier?: string | null;
    reasoningModel?: string;
    embeddingModel?: string;
}

/**
 * Spend over three spans, each including what has been used since the last spend line was written.
 * Months are calendar months in UTC, as AWS bills them.
 */
export interface SpendTotals {
    thisMonthUsd: number;
    last30DaysUsd: number;
    allUsd: number;
    /** When the first spend line began, or null when none has been written yet. */
    recordingSince: string | null;
}

export interface LLMUsageReport {
    provider: ProviderDescription;
    prices: ModelPrice[];
    startedAt: string;
    sinceStartup: ModelUsage[];
    runs: UsageRun[];
    runsInFile: number;
    /** The usage log's file name; where it lives on the server is not shown. */
    logFile: string | null;
    spend: SpendTotals;
}

const HEADERS = { headers: { 'Content-Type': MEDIA_TYPE_V2 }, withCredentials: true };

function extractMessage(error: unknown): string {
    if (axios.isAxiosError(error)) {
        const data = error.response?.data as { message?: string } | undefined;
        if (data?.message) return data.message;
        if (error.message) return error.message;
    }
    return error instanceof Error ? error.message : 'Unknown error';
}

class LlmUsageService {
    async getUsage(): Promise<LLMUsageReport> {
        try {
            const res = await axios.get<LLMUsageReport>(`${API_URL}api/v2/llm/usage`, HEADERS);
            return res.data;
        } catch (e) {
            throw new Error(extractMessage(e));
        }
    }
}

export default new LlmUsageService();
